import { InputGroup } from '@chakra-ui/react'
import { Flex, FlexProps, Input, VStack } from '@chakra-ui/react'
import React, {
  FocusEvent,
  forwardRef,
  Ref,
  useEffect,
  useId,
  useState,
} from 'react'
import { useScrollingInputDropdownStyle } from '~/components/shared/componentStyles/ScrollingInputDropdown.style'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import { LoadingDots } from '~/components/shared/LoadingDots'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { useUserClickedExternalElements } from '~/hooks/useUserClickedExternalElements'
import { tryGetValueOrDefault } from '~/utils/common'
import { castRef } from '~/utils/refUtils'

import { SelectItem } from './DropdownSelect'
import { Menu } from './Menu'

const COMBOBOX_AUTOCOMPLETE = 'list'

/**
 * ScrollingInputDropdownProps
 * @typedef {object} ScrollingInputDropdownProps
 * @property {string} [placeholder] - The placeholder text for the input
 * @property {string} [value] - The value of the input
 * @property {boolean} [disabled] - Whether the input is disabled
 * @property {boolean} [isOpen] - Whether the dropdown is open
 * @property {number} [selectedIndexOverride] - The index of the selected item
 * @property {boolean} [autoSetValueOnSelected] - Whether the input value should be set to the selected item.  Value must be a string for this to work.
 * @property {boolean} [autoCloseOnSelected] - Whether the dropdown should close when an item is selected
 * @property {boolean} [autoCloseOnBlur] - Whether the dropdown should close when the input loses focus
 * @property {boolean} [autoOpen] - Whether the dropdown should open automatically when items are present
 * @property {ScrollingInputItem} items - The items to display in the dropdown
 * @property {(value: string) => string | void} [onValueChanged] - The function to call when the input value changes.  If a string is returned, the input value will be set to that string.
 * @property {(item: ScrollingInputItem, index: number) => void} [onSelected] - The function to call when an item is selected
 * @property {() => void} [onScrollEndReached] - The function to call when the scroll end is reached
 * @property {{ height?: string, width?: string, matchWidth?: boolean }} [menuOptions] - The options for the dropdown menu
 * @property {string} [inputAriaLabel] - Accessible name for the combobox input
 * @property {string} [listboxAriaLabel] - Accessible name for the suggestion list
 * @property {(count: number) => string} [getResultsAnnouncement] - Text announced to screen readers when the suggestion count changes
 * @property {FlexProps} [rest] - Any additional Flex properties you want to modify for the wrapper of this component.
 */
type ScrollingInputDropdownProps<TValue> = FlexProps & {
  placeholder?: string
  value?: string
  valueIsExtension?: boolean
  disabled?: boolean
  isOpen?: boolean
  selectedIndexOverride?: number
  autoSetValueOnSelected?: boolean
  autoCloseOnSelected?: boolean
  autoCloseOnBlur?: boolean
  isLoading?: boolean
  isInvalid?: boolean
  items?: SelectItem<TValue>[]
  onFocus?: (e: FocusEvent<HTMLDivElement>) => void
  onValueChanged?: (value: string) => void
  onSelected?: (value: TValue, index: number) => string | void
  onScrollEndReached?: () => void
  onBlur?: () => void
  onMenuClose?: () => void
  menuOptions?: {
    height?: string
    width?: string
    matchWidth?: boolean
  }
  inputRef?: Ref<HTMLInputElement>
  inputAriaLabel?: string
  listboxAriaLabel?: string
  getResultsAnnouncement?: (count: number) => string
}

export const RefScrollingInputDropdown = forwardRef(
  <TValue,>(
    {
      placeholder = '',
      value: externalValue = '',
      defaultValue = undefined,
      disabled = false,
      isOpen = false,
      autoSetValueOnSelected,
      autoCloseOnSelected,
      autoCloseOnBlur = true,
      isLoading = false,
      isInvalid = false,
      items = [],
      onFocus,
      onBlur,
      onValueChanged,
      onScrollEndReached,
      onSelected,
      onMenuClose,
      inputAriaLabel,
      listboxAriaLabel,
      getResultsAnnouncement,
      ...rest
    }: ScrollingInputDropdownProps<TValue>,
    ref: Ref<HTMLInputElement>,
  ) => {
    const [inputValue, setInputValue] = useState<string>('')
    const [focused, setFocused] = useState<boolean>(false)
    const [activeIndex, setActiveIndex] = useState(-1)
    const [isDismissed, setIsDismissed] = useState(false)
    const listboxId = useId()
    const getOptionId = (index: number) => `${listboxId}-option-${index}`
    const announce = useAnnounce()
    const containerRef = React.useRef<HTMLDivElement>(null)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const styles = useScrollingInputDropdownStyle()

    const handleScrollEndReachedInternal = () => {
      //only report scroll end reached if there are items in the menu
      if (!items || items.length <= 0) {
        return
      }

      onScrollEndReached?.()
    }

    const handleInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsDismissed(false)
      setInputValue(e.target.value)
      onValueChanged?.(e.target.value)
      // Typing after the menu closed doesn't re-fire focus, so re-signal it.
      handleReopen(e)
    }

    const handleClose = () => {
      onMenuClose?.()
    }

    const handleInputFocus = (e: FocusEvent<HTMLDivElement>) => {
      setIsDismissed(false)
      setFocused(true)
      onFocus?.(e)
    }

    // A native focus event only fires the first time the input gains focus.
    // Once the menu closes while the input keeps DOM focus, clicking or typing
    // produces no focus event, leaving the menu stuck closed. Re-signal focus
    // on those interactions so the parent can reopen the menu.
    const handleReopen = (e: React.SyntheticEvent<HTMLDivElement>) => {
      setFocused(true)
      // Stand-in cast: change/click paths pass a Change/Mouse event, not a real
      // FocusEvent. Current consumers ignore the arg; a future onFocus that
      // reads e.relatedTarget/e.target must not rely on it here.
      onFocus?.(e as FocusEvent<HTMLDivElement>)
    }

    const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
      if (disabled) return
      setIsDismissed(false)
      handleReopen(e)
    }

    const handleInputFocusLost = () => {
      setFocused(false)
      onBlur?.()

      if (autoCloseOnBlur) {
        handleClose()
      }
    }

    const handleSelectedInternal = (value: TValue, index: number) => {
      const returnVal = onSelected?.(value, index)

      if (returnVal && typeof returnVal === 'string') {
        setInputValue(returnVal)
      } else {
        if (autoSetValueOnSelected && typeof value === 'string') {
          setInputValue(value)
        }
      }

      if (autoCloseOnSelected) {
        handleClose()
      }
    }

    useUserClickedExternalElements(
      [containerRef.current],
      true,
      handleInputFocusLost,
    )

    const keyPress = useKeyPresses()
    useEffect(() => {
      const shouldHandleBlur = keyPress?.key === 'Tab' && focused && isOpen
      if (shouldHandleBlur) {
        handleInputFocusLost()
      }
    }, [keyPress])

    const shouldBeOpen = isOpen && !disabled && !isDismissed
    const itemCount = items.length

    useEffect(() => {
      setActiveIndex(-1)
    }, [shouldBeOpen, itemCount])

    useEffect(() => {
      if (!shouldBeOpen || itemCount === 0 || !getResultsAnnouncement) return
      announce(getResultsAnnouncement(itemCount))
    }, [shouldBeOpen, itemCount])

    // Dialogs listen for Escape on the document in the capture phase, so the
    // list has to claim it earlier (window capture) to close itself only.
    useEffect(() => {
      if (!shouldBeOpen) return
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return
        const input = inputRef.current?.querySelector('input')
        if (!input || document.activeElement !== input) return
        e.preventDefault()
        e.stopPropagation()
        setIsDismissed(true)
      }
      window.addEventListener('keydown', handleEscape, true)
      return () => window.removeEventListener('keydown', handleEscape, true)
    }, [shouldBeOpen])

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          e.preventDefault()
          if (!shouldBeOpen) {
            setIsDismissed(false)
            handleReopen(e)
            return
          }
          if (itemCount === 0) return
          const lastIndex = itemCount - 1
          if (e.key === 'ArrowDown') {
            setActiveIndex(activeIndex >= lastIndex ? 0 : activeIndex + 1)
          } else {
            setActiveIndex(activeIndex <= 0 ? lastIndex : activeIndex - 1)
          }
          break
        }
        case 'Enter': {
          const activeItem = items[activeIndex]
          if (!shouldBeOpen || !activeItem) return
          e.preventDefault()
          if (activeItem.clickDisabled) return
          handleSelectedInternal(activeItem.value, activeIndex)
          activeItem.onSelect?.(activeItem.value, activeIndex)
          break
        }
      }
    }

    const derivedInputValue = defaultValue
      ? undefined
      : tryGetValueOrDefault(externalValue, inputValue)
    const disabledValue = defaultValue
      ? undefined
      : disabled
        ? ''
        : derivedInputValue

    return (
      <Flex ref={containerRef} {...rest} css={styles.container}>
        <VStack css={styles.vstack}>
          <InputGroup
            ref={inputRef}
            endElement={isLoading ? <LoadingDots /> : undefined}
          >
            <Input
              ref={ref}
              role="combobox"
              aria-label={inputAriaLabel}
              aria-autocomplete={COMBOBOX_AUTOCOMPLETE}
              aria-expanded={shouldBeOpen}
              aria-controls={listboxId}
              aria-activedescendant={
                shouldBeOpen && activeIndex >= 0
                  ? getOptionId(activeIndex)
                  : undefined
              }
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder}
              value={disabledValue}
              defaultValue={defaultValue}
              onChange={handleInputChanged}
              onFocus={handleInputFocus}
              onClick={handleInputClick}
              _invalid={isInvalid ? {} : undefined}
            />
          </InputGroup>
          {shouldBeOpen && (
            <Menu
              anchorRef={castRef(inputRef)}
              items={items}
              onSelect={handleSelectedInternal}
              onScrollEndReached={handleScrollEndReachedInternal}
              listbox={{
                id: listboxId,
                activeIndex,
                getOptionId,
                label: listboxAriaLabel,
              }}
            />
          )}
        </VStack>
      </Flex>
    )
  },
)

// Turns out getting a generic type for the ref is a little bit of a trick.
export const ScrollingInputDropdown = <T,>({
  inputRef,
  ...rest
}: ScrollingInputDropdownProps<T> & {
  inputRef?: React.Ref<HTMLInputElement>
}) => (
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  <RefScrollingInputDropdown {...rest} ref={inputRef} />
)
